import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useReportFavoritesStore } from '@/store/reportFavoritesStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import SyncModeBanner from '@/components/pos/SyncModeBanner';
import { useUIStore } from '@/store/uiStore';
import { 
  Menu, 
  Search, 
  Star, 
  CheckSquare, 
  TrendingUp, 
  FileText, 
  Package, 
  Users, 
  ShieldAlert, 
  UserSquare2, 
  Banknote, 
  RotateCcw, 
  BarChart, 
  PieChart, 
  Truck, 
  Hourglass
} from 'lucide-react';

type ReportCategory = 'All' | 'Sales' | 'Purchases' | 'Money' | 'Inventory' | 'Favourites';

interface ReportInfo {
  id: string;
  title: string;
  subtitle: string;
  path: string;
  category: Exclude<ReportCategory, 'All' | 'Favourites'>;
  icon: React.ElementType;
}

export default function ReportsDashboard() {
  const { toggleSidebar } = useUIStore();
  const { favorites, toggleFavorite, isFavorite } = useReportFavoritesStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<ReportCategory>('All');

  const ALL_REPORTS: ReportInfo[] = useMemo(() => [
    {
      id: 'daily-summary',
      title: 'Daily Summary',
      subtitle: 'One page for closing time, including what the drawer should hold',
      path: '/reports/sales/z-day',
      category: 'Sales',
      icon: CheckSquare
    },
    {
      id: 'sales-overview',
      title: 'Sales Overview',
      subtitle: 'Rozana sales, profit aur trend ek nazar mein',
      path: '/reports/sales/summary',
      category: 'Sales',
      icon: TrendingUp
    },
    {
      id: 'sale-register',
      title: 'Sale Register',
      subtitle: 'Every sale invoice with its payment status',
      path: '/reports/sales/register',
      category: 'Sales',
      icon: FileText
    },
    {
      id: 'item-sales',
      title: 'Item Sales',
      subtitle: 'What each item sold, and the profit it earned',
      path: '/reports/sales/items-summary',
      category: 'Inventory',
      icon: Package
    },
    {
      id: 'customer-sales',
      title: 'Customer Sales',
      subtitle: 'Har customer ki kharidari aur balance',
      path: '/reports/sales/party-summary',
      category: 'Sales',
      icon: Users
    },
    {
      id: 'customer-ledger',
      title: 'Customer Ledger',
      subtitle: 'Customer debit/credit ledger',
      path: '/reports/sales/customer-ledger',
      category: 'Sales',
      icon: FileText
    },
    {
      id: 'staff-watch',
      title: 'Staff Watch',
      subtitle: 'Worth a daily glance: price cuts, refunds and deletions',
      path: '/reports/sales/shift-performance', // Or a more specific report if one exists
      category: 'Sales',
      icon: ShieldAlert
    },
    {
      id: 'cashier-shift',
      title: 'Cashier & Shift',
      subtitle: 'Sales by cashier, with the day-close record',
      path: '/reports/sales/shift-performance',
      category: 'Sales',
      icon: UserSquare2
    },
    {
      id: 'customer-statement',
      title: 'Customer Statement',
      subtitle: 'One customer\'s full account, ready to hand over',
      path: '/reports/sales/activity-party',
      category: 'Sales',
      icon: FileText
    },
    {
      id: 'recovery-receipts',
      title: 'Recovery / Receipts',
      subtitle: 'Money recovered from customers',
      path: '/reports/sales/recovery',
      category: 'Money',
      icon: Banknote
    },
    {
      id: 'returns',
      title: 'Returns',
      subtitle: 'Goods returned, and what they were worth',
      path: '/reports/sales/returned-invoices',
      category: 'Sales',
      icon: RotateCcw
    },
    {
      id: 'sales-comparison',
      title: 'Sales Comparison',
      subtitle: 'This period measured against the one before',
      path: '/reports/analysis/shop-status', // Using closest equivalent
      category: 'Sales',
      icon: BarChart
    },
    {
      id: 'category-profit',
      title: 'Category Profit',
      subtitle: 'Which part of the shop actually earns',
      path: '/reports/sales/items-analysis',
      category: 'Sales',
      icon: PieChart
    },
    {
      id: 'supplier-summary',
      title: 'Supplier Summary',
      subtitle: 'Har supplier ka total aur outstanding',
      path: '/reports/purchases/supplier-summary',
      category: 'Purchases',
      icon: Truck
    },
    {
      id: 'supplier-statement',
      title: 'Supplier Statement',
      subtitle: 'One supplier\'s full account',
      path: '/reports/purchases/activity-supplier',
      category: 'Purchases',
      icon: FileText
    },
    {
      id: 'supplier-ledger',
      title: 'Supplier Ledger',
      subtitle: 'Supplier debit/credit ledger',
      path: '/reports/purchases/supplier-ledger',
      category: 'Purchases',
      icon: FileText
    },
    {
      id: 'payables-aging',
      title: 'Payables Aging',
      subtitle: 'What is owed to suppliers, and how long it has been due',
      path: '/reports/purchases/outstanding',
      category: 'Purchases',
      icon: Hourglass
    },
    {
      id: 'daily-cash-ledger',
      title: 'Cash Ledger',
      subtitle: 'Cash book of incoming and outgoing money',
      path: '/reports/daily-cash-ledger',
      category: 'Money',
      icon: Banknote
    },
    {
      id: 'cash-summary',
      title: 'Cash Summary',
      subtitle: 'Admin overview of Cash in Safe',
      path: '/reports/cash-summary',
      category: 'Money',
      icon: Banknote
    },
    {
      id: 'salary-report',
      title: 'Salary Report',
      subtitle: 'Track fixed salaries and salary expenses',
      path: '/reports/salary-report',
      category: 'Money',
      icon: Banknote
    },
    {
      id: 'profit-loss',
      title: 'Profit vs Loss',
      subtitle: 'Profit and loss statement based on actual item costs and expenses',
      path: '/reports/analysis/profit-loss',
      category: 'Money',
      icon: BarChart
    },
    {
      id: 'cash-flow',
      title: 'Cash Flow Dashboard',
      subtitle: 'Monitor your shop\'s cash movement',
      path: '/reports/analysis/cash-flow-dashboard',
      category: 'Money',
      icon: TrendingUp
    }
  ], []);

  const filteredReports = useMemo(() => {
    return ALL_REPORTS.filter((report) => {
      // 1. Filter by Search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = report.title.toLowerCase().includes(query) || report.subtitle.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // 2. Filter by Category
      if (activeCategory === 'Favourites') {
        if (!isFavorite(report.id)) return false;
      } else if (activeCategory !== 'All') {
        if (report.category !== activeCategory) return false;
      }

      return true;
    });
  }, [ALL_REPORTS, searchQuery, activeCategory, favorites, isFavorite]);

  const groupedReports = useMemo(() => {
    if (activeCategory === 'Favourites' || searchQuery.trim()) {
      // Don't group strictly by category if searching or viewing favorites, just show a single group
      return { 'Reports': filteredReports };
    }
    
    // Group by category when viewing 'All' or a specific category
    const groups: Record<string, ReportInfo[]> = {};
    filteredReports.forEach((report) => {
      if (!groups[report.category]) groups[report.category] = [];
      groups[report.category].push(report);
    });
    return groups;
  }, [filteredReports, activeCategory, searchQuery]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <SyncModeBanner />
        <TrialBanner />
        <div className="p-4 sm:p-6 space-y-6 sm:space-y-8 max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Reports</h1>
              <p className="text-gray-500 text-sm mt-1">Every report about your business, in one place.</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="space-y-4">
            <div className="relative max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search reports... (e.g. stock, profit, outstanding)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm shadow-sm transition-all duration-200 hover:border-gray-400"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(['All', 'Sales', 'Purchases', 'Money', 'Inventory'] as ReportCategory[]).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
              <button
                onClick={() => setActiveCategory('Favourites')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  activeCategory === 'Favourites'
                    ? 'bg-amber-100 text-amber-700 border border-amber-200 shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Star className={`w-4 h-4 ${activeCategory === 'Favourites' ? 'fill-amber-500 text-amber-500' : 'text-amber-400 fill-amber-400'}`} />
                Favourites
              </button>
            </div>
          </div>

          {/* Reports Grid */}
          <div className="space-y-8 pb-12">
            {Object.entries(groupedReports).map(([groupName, reports]) => (
              reports.length > 0 && (
                <div key={groupName} className="space-y-4">
                  {groupName !== 'Reports' && (
                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{groupName}</h2>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        className="group relative bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all duration-200 flex flex-col h-full"
                      >
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="p-2.5 rounded-xl bg-primary-50 text-primary-600 group-hover:scale-110 transition-transform duration-200">
                            <report.icon className="w-5 h-5" />
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite(report.id);
                            }}
                            className="relative z-10 p-1.5 rounded-lg text-gray-300 hover:text-amber-400 hover:bg-amber-50 transition-colors"
                          >
                            <Star className={`w-5 h-5 ${isFavorite(report.id) ? 'fill-amber-400 text-amber-400' : ''}`} />
                          </button>
                        </div>
                        
                        <Link to={report.path} className="flex-1 focus:outline-none">
                          <span className="absolute inset-0" aria-hidden="true" />
                          <h3 className="text-base font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                            {report.title}
                          </h3>
                          <p className="mt-1.5 text-sm text-gray-500 line-clamp-3">
                            {report.subtitle}
                          </p>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
            
            {filteredReports.length === 0 && (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900">No reports found</h3>
                <p className="text-gray-500 mt-1">Try adjusting your search or filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
